import { type CSSProperties, type FC, type Ref, useMemo } from "react";
import Select, { type GroupBase, type SelectInstance, type StylesConfig } from "react-select";

interface Category {
  id: number;
  name: string;
  isGroup?: boolean;
  groupId?: number;
  sortOrder?: number;
}

interface CategoryOption {
  label: string;
  value: number;
  groupId?: number;
  isGroup?: boolean;
  sortOrder?: number;
  options?: CategoryOption[];
}

interface CategorySelectProps {
  categories: Category[];
  value?: { label: string; value: number } | null;
  onChange: (value: { label: string; value: number } | null) => void;
  inputRef?: Ref<SelectInstance<CategoryOption, false, GroupBase<CategoryOption>>>;
}

const groupStyles: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const formatGroupLabel = (data: GroupBase<CategoryOption>) => (
  <div style={groupStyles}>
    <span>{data.label}</span>
  </div>
);

export const CategorySelect: FC<CategorySelectProps> = ({
  categories,
  value,
  onChange,
  inputRef,
}) => {
  const options: CategoryOption[] = useMemo(
    () =>
      categories.map((item) => ({
        label: item.name,
        value: item.id,
        isGroup: item.isGroup,
        groupId: item.groupId,
        sortOrder: item.sortOrder,
      })),
    [categories],
  );

  const groupedOptions: CategoryOption[] = useMemo(() => {
    const groups = options
      .filter((item) => item.isGroup)
      .map((item) => ({
        ...item,
        options: [] as CategoryOption[],
      }))
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

    const ungroupedItems: CategoryOption[] = [];

    for (const item of options.filter((item) => !item.isGroup)) {
      const parentIndex = groups.findIndex((option) => option.value === item.groupId);
      if (parentIndex !== -1) {
        groups[parentIndex].options?.push(item);
      } else {
        ungroupedItems.push(item);
      }
    }
    return [...ungroupedItems, ...groups];
  }, [options]);

  const style: StylesConfig<CategoryOption, false> = {
    input: (base) => ({
      ...base,
      ":focus": {
        boxShadow: "none",
      },
    }),
  };

  return (
    <div>
      <Select<CategoryOption, false, GroupBase<CategoryOption>>
        options={groupedOptions}
        value={value}
        onChange={onChange}
        formatGroupLabel={formatGroupLabel}
        styles={style}
        ref={inputRef}
        placeholder="Select category..."
      />
    </div>
  );
};
